import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import ExportTypeSelector from 'src/clinic/views/clinicBrandPayoutReports/components/ExportTypeSelector'
import { downloadExcelSheetFromRows } from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getManagementBookingReportingClinicBrands,
  getManagementBookingReportingFilterOptions,
  getManagementBookingReportingMonthlySummary,
  getManagementBookingReportingOverall,
  getManagementBookingReportingPractitionerRevenueDetails,
  getManagementBookingReportingPractitioners,
  getManagementBookingReportingQuarterlySummary,
  refreshManagementBookingReportingSnapshots,
  saveManagementBookingReportingSnapshot,
} from 'src/supportPortal/services/bookingReportingService'

const EVENT_FILTER_OPTIONS = [
  { value: '', label: 'Select event type to start' },
  { value: 'all', label: 'All supported events' },
  { value: 'therapy', label: 'Therapy sessions' },
  { value: 'consultation', label: 'Consultations' },
  { value: 'screening', label: 'Screenings' },
  { value: 'assessment', label: 'Assessments' },
]

const DATE_DIMENSION_OPTIONS = [
  { value: 'booking', label: 'Booking date' },
  { value: 'scheduled', label: 'Scheduled date' },
]

const AFFILIATION_FILTER_OPTIONS = [
  { value: 'aumhum', label: 'AUMHUM affiliated only' },
  { value: 'clinic', label: 'Clinic affiliated only' },
  { value: 'all', label: 'All affiliations' },
]

const TAB_OPTIONS = [
  { value: 'overall', label: 'Overall' },
  { value: 'practitioners', label: 'Practitioner' },
  { value: 'clinicBrands', label: 'Clinic Brand' },
  { value: 'quarterly', label: 'QoQ Summary' },
  { value: 'monthly', label: 'MoM Summary' },
]

const SUMMARY_CARD_FIELDS = [
  { key: 'totalBookings', label: 'Bookings' },
  { key: 'paidBookings', label: 'Paid' },
  { key: 'startedSessions', label: 'Started' },
  { key: 'completedSessions', label: 'Completed' },
  { key: 'neverStartedSessions', label: 'Never started' },
  { key: 'cancelledSessions', label: 'Cancelled' },
  { key: 'rescheduledSessions', label: 'Rescheduled' },
  { key: 'noShowSessions', label: 'No-shows' },
]

const RATE_CARD_FIELDS = [
  { key: 'bookingToPaymentConversionRate', label: 'Booking to payment', type: 'rate' },
  { key: 'paymentToAttendanceConversionRate', label: 'Payment to attendance', type: 'rate' },
  { key: 'bookingToCompletionConversionRate', label: 'Booking to completion', type: 'rate' },
  { key: 'completionRate', label: 'Completion rate', type: 'rate' },
  { key: 'cancellationRate', label: 'Cancellation rate', type: 'rate' },
  { key: 'rescheduleRate', label: 'Reschedule rate', type: 'rate' },
  { key: 'noShowRate', label: 'No-show rate', type: 'rate' },
]

const JOIN_CARD_FIELDS = [
  { key: 'patientJoinedSessions', label: 'Patient joined' },
  { key: 'practitionerJoinedSessions', label: 'Practitioner joined' },
  { key: 'bothJoinedSessions', label: 'Both joined' },
  { key: 'onePartyJoinedSessions', label: 'One-party joined' },
  { key: 'onePartyJoinedRate', label: 'One-party joined rate', type: 'rate' },
  { key: 'avgPatientJoinDelayMinutes', label: 'Avg patient join delay (min)', type: 'number' },
  { key: 'avgPractitionerJoinDelayMinutes', label: 'Avg practitioner join delay (min)', type: 'number' },
]

const REVENUE_CARD_FIELDS = [
  { key: 'revenueBooked', label: 'Revenue booked', type: 'currency' },
  { key: 'revenueCollected', label: 'Revenue collected', type: 'currency' },
  { key: 'revenueToAumhum', label: 'Revenue to AUMHUM', type: 'currency' },
  { key: 'payoutToPractitioner', label: 'Payout to practitioner', type: 'currency' },
  { key: 'revenueRealized', label: 'Revenue realized', type: 'currency' },
  { key: 'repeatSessions', label: 'Repeat sessions', type: 'number' },
  { key: 'repeatSessionContributionRate', label: 'Repeat session contribution', type: 'rate' },
]

const PATIENT_CONTINUITY_FIELDS = [
  { key: 'distinctPatients', label: 'Distinct patients' },
  { key: 'avgSessionsPerPatient', label: 'Avg sessions / patient', type: 'number' },
  { key: 'oneSessionOnlyPatients', label: '1 session only' },
  { key: 'repeatPatients', label: '2+ sessions (repeat)' },
  { key: 'fourPlusSessionPatients', label: '4+ sessions' },
  { key: 'longTermCarePatients', label: '10+ sessions' },
  { key: 'repeatPatientShare', label: 'Repeat patient rate', type: 'rate' },
  { key: 'longTermCarePatientShare', label: '10+ session share', type: 'rate' },
]

const COHORT_THRESHOLD_CARDS = [
  {
    key: 'oneSessionOnlyPatients',
    shareKey: 'oneSessionOnlyPatientShare',
    label: '1 session only',
    subtitle: 'Patients whose highest valid booked session number is exactly 1.',
  },
  {
    key: 'repeatPatients',
    shareKey: 'repeatPatientShare',
    label: '2+ sessions (repeat)',
    subtitle: 'Patients whose highest valid booked session number is 2 or more.',
  },
  {
    key: 'fourPlusSessionPatients',
    shareKey: 'fourPlusSessionPatientShare',
    label: '4+ sessions',
    subtitle: 'Patients whose highest valid booked session number is 4 or more.',
  },
  {
    key: 'longTermCarePatients',
    shareKey: 'longTermCarePatientShare',
    label: '10+ sessions',
    subtitle: 'Patients whose highest valid booked session number is 10 or more.',
  },
]

const SUMMARY_PAGE_SIZES = {
  quarterly: 4,
  monthly: 6,
}

const METRIC_DEFINITIONS = {
  totalBookings:
    'All non-test booked events in the selected event-type slice. Example: if 42 therapy bookings fall inside the window, bookings = 42.',
  paidBookings:
    'Bookings whose payment status is paid. Example: 30 of 42 bookings were paid, so paid bookings = 30.',
  startedSessions:
    'Bookings where the session actually started, based on BookedEventLog joins or explicit started markers. Example: the room opens and the encounter begins, so the booking counts as started.',
  completedSessions:
    'Sessions marked completed or finished. Example: a started therapy session closes successfully and moves to finished.',
  startedCompleted:
    'Shows started volume alongside completed volume and the completion rate. Example: 20 started and 16 completed means some sessions began but did not complete.',
  neverStartedSessions:
    'Booked sessions that never moved into a started state. Example: a paid booking where nobody joins and no session-start marker exists.',
  cancelledSessions:
    'Bookings whose final booking status is cancelled. Example: the patient or practitioner cancels before the session begins.',
  rescheduledSessions:
    'Bookings that show a reschedule action in modification history. Example: Tuesday was moved to Friday.',
  noShowSessions:
    'Bookings marked as patient no-show or practitioner no-show. Example: the patient misses the appointment and support marks a no-show.',
  bookingToPaymentConversionRate:
    'Paid bookings divided by total bookings. Example: 30 paid out of 42 booked gives a 71.4% booking-to-payment conversion.',
  paymentToAttendanceConversionRate:
    'Attended sessions divided by paid bookings. Example: 24 attended sessions out of 30 paid bookings gives 80%.',
  bookingToCompletionConversionRate:
    'Completed sessions divided by total bookings. Example: 24 completed out of 42 bookings gives 57.1%.',
  completionRate:
    'Completed sessions divided by total bookings. Example: 16 completed out of 20 bookings gives 80%.',
  cancellationRate:
    'Cancelled bookings divided by total bookings. Example: 4 cancelled out of 40 bookings gives 10%.',
  rescheduleRate:
    'Rescheduled bookings divided by total bookings. Example: 5 rescheduled out of 40 bookings gives 12.5%.',
  noShowRate:
    'No-show bookings divided by total bookings. Example: 2 no-shows out of 40 bookings gives 5%.',
  patientJoinedSessions:
    'Sessions where the patient joined, using BookedEventLog. Example: the patient enters the room even if the practitioner never joins.',
  practitionerJoinedSessions:
    'Sessions where the relevant practitioner joined, using BookedEventLog. Example: doctor, therapist, or screener joins the room.',
  bothJoinedSessions:
    'Sessions where both patient and practitioner joined. Example: both enter the call room, so the session counts as both joined.',
  onePartyJoinedSessions:
    'Sessions where exactly one side joined. Example: the patient joins but the practitioner never joins.',
  onePartyJoinedRate:
    'One-party joined sessions divided by total bookings. Example: 3 one-party joins out of 30 bookings gives 10%.',
  avgPatientJoinDelayMinutes:
    'Average minutes after scheduled start when the patient joined. Example: patient joins at 10:03 for a 10:00 session, so delay = 3 minutes.',
  avgPractitionerJoinDelayMinutes:
    'Average minutes after scheduled start when the practitioner joined. Example: therapist joins at 10:01 for a 10:00 session, so delay = 1 minute.',
  joinDelays:
    'Shows patient and practitioner average join delays together for quick comparison. Example: patient 4 min and practitioner 1 min indicates more patient-side delay.',
  revenueBooked:
    'Booked value before fulfillment, using paid amount first and list price as fallback. Example: a Rs 1,000 paid consultation contributes Rs 1,000.',
  revenueCollected:
    'Collected amount normalized into actual currency units. Example: a Razorpay value stored as 97050 paise is shown as Rs 970.50.',
  revenueToAumhum:
    'Share attributable to AUMHUM after payout-model calculations. Example: if Rs 1,000 is collected and practitioner payout is Rs 400, AUMHUM revenue may be Rs 600 before other source-share adjustments.',
  payoutToPractitioner:
    'Share attributable to the practitioner from payout calculations. Example: if a therapist is owed Rs 400 for the session, payout to practitioner = Rs 400.',
  revenueRealized:
    'Collected revenue from completed sessions only. Example: if Rs 5,000 is collected but only Rs 4,200 belongs to completed sessions, realized revenue = Rs 4,200.',
  distinctPatients:
    'Unique patients in the booked-status cohort base for the selected slice. Example: 18 unique patients booked at least one valid session in the period.',
  avgSessionsPerPatient:
    'Booked-status session volume divided by distinct patients. Example: 36 valid booked sessions across 18 patients gives 2 sessions per patient.',
  repeatPatients:
    'Patients whose highest valid booked session number is 2 or more. Example: a patient on session 3 counts as repeat.',
  repeatPatientShare:
    'Repeat patients divided by distinct patients. Example: 12 repeat patients out of 18 patients gives 66.7%.',
  repeatSessions:
    'Booked sessions whose session number is above 1. Example: session 2, 3, and 4 bookings all count as repeat sessions.',
  repeatSessionContributionRate:
    'Repeat sessions divided by booked-status session volume. Example: 20 repeat sessions out of 36 valid booked sessions gives 55.6%.',
  oneSessionOnlyPatients:
    'Patients whose highest valid booked session number is exactly 1. Example: a patient who booked only the first session stays in this cohort.',
  oneSessionOnlyPatientShare:
    '1-session-only patients divided by the booked-status patient cohort base. Example: 6 of 18 patients gives 33.3%.',
  fourPlusSessionPatients:
    'Patients whose highest valid booked session number is 4 or more. Example: session 4, 5, or 10 patients all count here.',
  fourPlusSessionPatientShare:
    '4+ session patients divided by the booked-status patient cohort base. Example: 5 of 18 patients gives 27.8%.',
  patientsContinuingBeyondFourSessions:
    'Legacy continuity metric preserved for compatibility. It now reflects the 4+ session threshold.',
  patientsContinuingBeyondFourShare:
    'Legacy continuity share preserved for compatibility. It now reflects the 4+ session threshold.',
  longTermCarePatients:
    'Patients whose highest valid booked session number is 10 or more. Example: a patient attending session 12 counts in long-term care.',
  longTermCarePatientShare:
    '10+ session patients divided by the booked-status patient cohort base. Example: 2 out of 18 gives 11.1%.',
  clinicBrandName:
    'Clinic brand rollup for the selected slice. Example: Manasthan Clinics total aggregates all included locations together.',
  clinicLocationName:
    'Brand total or individual clinic location under a brand. Example: Whitefield branch is shown separately below the brand total.',
  scope:
    'Shows whether the row is a clinic-brand total or a single location row. Example: Brand total summarizes the full brand while Whitefield is a location-level row.',
  periodLabel:
    'Completed reporting period for the selected event type. Example: 2026-Q1 or 2026-03.',
  periodWindow:
    'Exact date window covered by the completed reporting period. Example: 2026-01-01 to 2026-03-31.',
  servedFromCache:
    'Shows whether the period summary came from a saved snapshot or a live computation. Example: Cached means a stored monthly or quarterly snapshot was used.',
  revenueCollectedDetails:
    'Opens the event-level collected-revenue drilldown for this practitioner. Example: click the amount to inspect patient, clinic, session cohort, and event-level revenue.',
}

function getLastCompletedMonthWindow(value = dayjs()) {
  const lastCompletedDay = value.startOf('month').subtract(1, 'day')
  return {
    fromDate: lastCompletedDay.startOf('month').format('YYYY-MM-DD'),
    toDate: lastCompletedDay.endOf('month').format('YYYY-MM-DD'),
  }
}

function formatNumber(value, maximumFractionDigits = 0) {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits }).format(Number(value))
}

function formatRate(value) {
  if (value === null || value === undefined) {
    return 'Unavailable'
  }
  return `${formatNumber(Number(value) * 100, 1)}%`
}

function formatCurrency(value, currencyCode = 'INR') {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return '-'
  }
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currencyCode || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue)
  } catch (error) {
    return `${currencyCode || 'INR'} ${formatNumber(numericValue, 2)}`
  }
}

function formatDateTime(value) {
  if (!value) {
    return '-'
  }
  const formatted = dayjs(value)
  return formatted.isValid() ? formatted.format('DD MMM YYYY, hh:mm A') : '-'
}

function formatValue(value, type = 'number', currencyCode = 'INR') {
  if (value === null || value === undefined) {
    return type === 'rate' ? 'Unavailable' : '-'
  }
  if (type === 'rate') {
    return formatRate(value)
  }
  if (type === 'currency') {
    return formatCurrency(value, currencyCode)
  }
  if (type === 'number') {
    return formatNumber(value, 2)
  }
  return String(value)
}

function getPractitionerFilterOptionLabel(option) {
  const name = option?.practitionerName || ''
  const roles = option?.practitionerRoles ? ` • ${option.practitionerRoles}` : ''
  return `${name}${roles}`.trim()
}

function getClinicBrandFilterOptionLabel(option) {
  return option?.clinicBrandName || ''
}

function getMatchingBookingsLabel(option) {
  if (!option?.bookingCount) {
    return ''
  }
  return `${formatNumber(option.bookingCount)} matching bookings`
}
function extractPayload(response, key) {
  return getDataMapFromCommonResponse(response)?.[key] || {}
}

function extractSummaryRowValue(row, sourceKey, key) {
  return sourceKey ? row?.[sourceKey]?.[key] : row?.[key]
}

function formatStartedCompletedExport(row, sourceKey) {
  const started = extractSummaryRowValue(row, sourceKey, 'startedSessions') || 0
  const completed = extractSummaryRowValue(row, sourceKey, 'completedSessions') || 0
  const completionRate = extractSummaryRowValue(row, sourceKey, 'completionRate')
  return `Started ${formatNumber(started)} / Completed ${formatNumber(completed)} (${formatRate(completionRate || 0)})`
}

function formatCohortExport(row, key, shareKey, sourceKey) {
  const count = extractSummaryRowValue(row, sourceKey, key) || 0
  const share = extractSummaryRowValue(row, sourceKey, shareKey)
  return `${formatNumber(count)} (${formatRate(share || 0)})`
}

function resolveColumnExportValue(row, column) {
  if (column.exportValue) {
    return column.exportValue(row)
  }
  const valueSource = column.sourceKey ? row?.[column.sourceKey] : row
  return formatValue(
    valueSource?.[column.key],
    column.type,
    valueSource?.currencyCode || row?.currencyCode || 'INR',
  )
}

function sanitizeFileTitle(value) {
  return String(value || 'management-reporting')
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
}

async function exportDomAsPdf(node, fileTitle) {
  if (!node) {
    return
  }
  const { jsPDF } = await import('jspdf')
  const html2canvas = (await import('html2canvas')).default
  const canvas = await html2canvas(node, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
  })
  const imgData = canvas.toDataURL('image/png')
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 18
  const imgWidth = pageWidth - margin * 2
  const imgHeight = (canvas.height * imgWidth) / canvas.width

  let heightLeft = imgHeight
  let position = margin

  pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
  heightLeft -= pageHeight - margin * 2

  while (heightLeft > 0) {
    position = heightLeft - imgHeight + margin
    pdf.addPage()
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
    heightLeft -= pageHeight - margin * 2
  }

  pdf.save(`${fileTitle}.pdf`)
}

function DefinitionLabel({ metricKey, label }) {
  const definition = METRIC_DEFINITIONS[metricKey]
  if (!definition) {
    return label
  }
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Typography variant="body2" sx={{ maxWidth: 360 }}>
          {definition}
        </Typography>
      }
    >
      <Box
        component="span"
        sx={{
          textDecoration: 'underline dotted',
          textUnderlineOffset: '3px',
          cursor: 'help',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  )
}

function InsightSurface({ children, title, subtitle, surfaceRef }) {
  return (
    <Paper
      ref={surfaceRef}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
      }}
    >
      {title ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {children}
    </Paper>
  )
}

function DataTable({
  title,
  subtitle,
  rows = [],
  columns = [],
  getRowKey,
  getRowSx,
  headerAction,
  footer,
  emptyMessage,
  surfaceRef,
}) {
  return (
    <Paper
      ref={surfaceRef}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(15, 23, 42, 0.08)', background: '#f8fafc' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {headerAction}
        </Stack>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <DefinitionLabel metricKey={column.metricKey || column.key} label={column.label} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  {emptyMessage || 'No data found for the selected window.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={getRowKey ? getRowKey(row, index) : `${row?.label || row?.practitionerId || row?.clinicBrandId || index}`}
                  hover
                  sx={getRowSx ? getRowSx(row) : undefined}
                >
                  {columns.map((column) => {
                    if (column.render) {
                      return (
                        <TableCell key={column.key} sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {column.render(row)}
                        </TableCell>
                      )
                    }
                    const valueSource = column.sourceKey ? row?.[column.sourceKey] : row
                    return (
                      <TableCell key={column.key} sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {formatValue(
                          valueSource?.[column.key],
                          column.type,
                          valueSource?.currencyCode || row?.currencyCode || 'INR',
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {footer ? <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>{footer}</Box> : null}
    </Paper>
  )
}

function MetricCards({ title, subtitle, fields, summary }) {
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {fields.map((field) => (
          <Card
            key={field.key}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.75 }}>
                <DefinitionLabel metricKey={field.key} label={field.label} />
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatValue(summary?.[field.key], field.type || 'number', summary?.currencyCode || 'INR')}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  )
}

function ThresholdCohortCard({ summary, item }) {
  const count = Number(summary?.[item.key] || 0)
  const share = summary?.[item.shareKey]
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,249,255,0.98) 100%)',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
          <DefinitionLabel metricKey={item.key} label={item.label} />
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {formatNumber(count)}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {item.subtitle}
        </Typography>
        <Chip
          size="small"
          label={count > 0 || share ? formatRate(share || 0) : 'No patients yet'}
          sx={{ mt: 1.5 }}
        />
      </CardContent>
    </Card>
  )
}

function PatientDistributionPanel({ summary }) {
  return (
    <InsightSurface
      title="Patient distribution & continuity"
      subtitle="These cohorts are based only on booked-status events and patient session sequence, so they stay meaningful even when cancellations exist in the same period."
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {COHORT_THRESHOLD_CARDS.map((item) => (
          <ThresholdCohortCard key={item.key} summary={summary} item={item} />
        ))}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
        <Chip label={`Booked-status patient cohort base: ${formatNumber(summary?.bookedPatientCohortBase || summary?.patientsWithCompletedSessions || 0)}`} />
        <Chip label={`Avg sessions / patient: ${formatValue(summary?.avgSessionsPerPatient, 'number')}`} />
        <Chip label={`Repeat session contribution: ${formatRate(summary?.repeatSessionContributionRate || 0)}`} />
        <Chip label={`Distinct patients: ${formatNumber(summary?.distinctPatients || 0)}`} />
      </Stack>
    </InsightSurface>
  )
}

function renderStartedCompletedCell(row, sourceKey) {
  const source = sourceKey ? row?.[sourceKey] : row
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Started {formatNumber(source?.startedSessions || 0)} / Completed {formatNumber(source?.completedSessions || 0)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Completion rate {formatRate(source?.completionRate || 0)}
      </Typography>
    </Stack>
  )
}

function renderCohortThresholdCell(row, key, shareKey, sourceKey) {
  const source = sourceKey ? row?.[sourceKey] : row
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {formatNumber(source?.[key] || 0)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {formatRate(source?.[shareKey] || 0)}
      </Typography>
    </Stack>
  )
}

function renderJoinDelayCell(row, sourceKey) {
  const source = sourceKey ? row?.[sourceKey] : row
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Patient {formatValue(source?.avgPatientJoinDelayMinutes, 'number')}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Practitioner {formatValue(source?.avgPractitionerJoinDelayMinutes, 'number')}
      </Typography>
    </Stack>
  )
}

function renderScopeCell(row) {
  return row?.rowType === 'brandTotal' ? (
    <Chip size="small" label="Brand total" color="primary" variant="outlined" />
  ) : (
    row?.clinicLocationName || '-'
  )
}

function buildOverallExportRows(summary) {
  const sections = [
    { section: 'Volume', fields: SUMMARY_CARD_FIELDS },
    { section: 'Conversion & reliability', fields: RATE_CARD_FIELDS },
    { section: 'Join behavior', fields: JOIN_CARD_FIELDS },
    { section: 'Revenue', fields: REVENUE_CARD_FIELDS },
    { section: 'Patient continuity', fields: PATIENT_CONTINUITY_FIELDS },
  ]

  return sections.flatMap((section) =>
    section.fields.map((field) => ({
      section: section.section,
      metric: field.label,
      value: formatValue(summary?.[field.key], field.type || 'number', summary?.currencyCode || 'INR'),
    })),
  )
}

function getEventFilterLabel(value) {
  return EVENT_FILTER_OPTIONS.find((item) => item.value === value)?.label || 'Select event type to start'
}

function getDateDimensionLabel(value) {
  return DATE_DIMENSION_OPTIONS.find((item) => item.value === value)?.label || 'Booking date'
}

function getAffiliationFilterLabel(value) {
  return AFFILIATION_FILTER_OPTIONS.find((item) => item.value === value)?.label || 'AUMHUM affiliated only'
}

function buildSummaryExportRows(periods = []) {
  return periods.map((row) => ({
    period: row?.label || '-',
    window: `${row?.fromDate || '-'} to ${row?.toDate || '-'}`,
    bookings: formatNumber(row?.summary?.totalBookings || 0),
    startedCompleted: formatStartedCompletedExport(row, 'summary'),
    neverStarted: formatNumber(row?.summary?.neverStartedSessions || 0),
    onePartyJoinedRate: formatRate(row?.summary?.onePartyJoinedRate || 0),
    revenueCollected: formatCurrency(row?.summary?.revenueCollected || 0, row?.summary?.currencyCode || 'INR'),
    revenueToAumhum: formatCurrency(row?.summary?.revenueToAumhum || 0, row?.summary?.currencyCode || 'INR'),
    payoutToPractitioner: formatCurrency(
      row?.summary?.payoutToPractitioner || 0,
      row?.summary?.currencyCode || 'INR',
    ),
    patients: formatNumber(row?.summary?.distinctPatients || 0),
    oneSessionOnly: formatCohortExport(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare', 'summary'),
    repeatPatients: formatCohortExport(row, 'repeatPatients', 'repeatPatientShare', 'summary'),
    fourPlusSessions: formatCohortExport(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare', 'summary'),
    tenPlusSessions: formatCohortExport(row, 'longTermCarePatients', 'longTermCarePatientShare', 'summary'),
    source: row?.servedFromCache ? 'Cached' : 'Live',
  }))
}

export default function BookingReportingPage() {
  const today = useMemo(() => dayjs(), [])
  const latestCompletedMonthWindow = useMemo(() => getLastCompletedMonthWindow(today), [today])
  const summaryReferenceDate = useMemo(() => today.format('YYYY-MM-DD'), [today])
  const defaultFilters = useMemo(
    () => ({
      eventTypeFilter: '',
      dateDimension: 'scheduled',
      affiliationFilter: 'aumhum',
      fromDate: latestCompletedMonthWindow.fromDate,
      toDate: latestCompletedMonthWindow.toDate,
      practitionerId: '',
      clinicBrandId: '',
    }),
    [latestCompletedMonthWindow],
  )

  const overallExportRef = useRef(null)
  const practitionersExportRef = useRef(null)
  const clinicsExportRef = useRef(null)
  const quarterlyExportRef = useRef(null)
  const monthlyExportRef = useRef(null)
  const revenueExportRef = useRef(null)
  const primaryTabCacheRef = useRef({})
  const summaryTabCacheRef = useRef({})
  const filterOptionsCacheRef = useRef({})
  const filterOptionsRequestRef = useRef(0)

  const [selectedTab, setSelectedTab] = useState('overall')
  const [draftFilters, setDraftFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(null)
  const [periodPaging, setPeriodPaging] = useState({
    quarterly: { nextPage: 0, hasMore: false },
    monthly: { nextPage: 0, hasMore: false },
  })
  const [tabLoading, setTabLoading] = useState({
    overall: false,
    practitioners: false,
    clinicBrands: false,
    quarterly: false,
    monthly: false,
  })
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refreshingSnapshots, setRefreshingSnapshots] = useState(false)
  const [error, setError] = useState('')
  const [saveMessage, setSaveMessage] = useState('')
  const [reportData, setReportData] = useState({
    overall: {},
    practitioners: {},
    clinicBrands: {},
    quarterlySummary: {},
    monthlySummary: {},
  })
  const [filterOptions, setFilterOptions] = useState({
    practitionerOptions: [],
    clinicBrandOptions: [],
  })
  const [revenueDrawer, setRevenueDrawer] = useState({
    open: false,
    loading: false,
    error: '',
    payload: null,
  })

  const summaryLoading = {
    quarterly: tabLoading.quarterly,
    monthly: tabLoading.monthly,
  }
  const loading = Object.values(tabLoading).some(Boolean)
  const hasAppliedFilters = Boolean(appliedFilters?.eventTypeFilter)
  const filtersDirty = hasAppliedFilters
    ? draftFilters.eventTypeFilter !== appliedFilters.eventTypeFilter ||
      draftFilters.dateDimension !== appliedFilters.dateDimension ||
      draftFilters.affiliationFilter !== appliedFilters.affiliationFilter ||
      draftFilters.fromDate !== appliedFilters.fromDate ||
      draftFilters.toDate !== appliedFilters.toDate ||
      draftFilters.practitionerId !== appliedFilters.practitionerId ||
      draftFilters.clinicBrandId !== appliedFilters.clinicBrandId
    : Boolean(draftFilters.eventTypeFilter)

  const applyDisabled =
    loading ||
    !draftFilters.eventTypeFilter ||
    !draftFilters.fromDate ||
    !draftFilters.toDate ||
    draftFilters.fromDate > draftFilters.toDate

  const overallSummary = reportData?.overall?.summary || {}
  const practitionerRows = reportData?.practitioners?.rows || []
  const clinicBrandRows = reportData?.clinicBrands?.rows || []
  const quarterlyRows = reportData?.quarterlySummary?.periods || []
  const monthlyRows = reportData?.monthlySummary?.periods || []
  const practitionerRevenueEvents = revenueDrawer?.payload?.events || []

  function updateDraftFilter(key, value) {
    setSaveMessage('')
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }))
  }

  function getPrimaryCacheKey(filters) {
    return JSON.stringify({
      eventTypeFilter: filters?.eventTypeFilter || '',
      dateDimension: filters?.dateDimension || '',
      affiliationFilter: filters?.affiliationFilter || '',
      fromDate: filters?.fromDate || '',
      toDate: filters?.toDate || '',
      practitionerId: filters?.practitionerId || '',
      clinicBrandId: filters?.clinicBrandId || '',
    })
  }

  function getSummaryCacheKey(filters) {
    return JSON.stringify({
      eventTypeFilter: filters?.eventTypeFilter || '',
      dateDimension: filters?.dateDimension || '',
      affiliationFilter: filters?.affiliationFilter || '',
      practitionerId: filters?.practitionerId || '',
      clinicBrandId: filters?.clinicBrandId || '',
    })
  }

  function getFilterOptionsCacheKey(filters) {
    return JSON.stringify({
      eventTypeFilter: filters?.eventTypeFilter || '',
      dateDimension: filters?.dateDimension || '',
      affiliationFilter: filters?.affiliationFilter || '',
      fromDate: filters?.fromDate || '',
      toDate: filters?.toDate || '',
      practitionerId: filters?.practitionerId || '',
      clinicBrandId: filters?.clinicBrandId || '',
    })
  }

  function buildSharedParams(filtersToLoad = appliedFilters || draftFilters) {
    return {
      eventTypeFilter: filtersToLoad?.eventTypeFilter,
      dateDimension: filtersToLoad?.dateDimension,
      affiliationFilter: filtersToLoad?.affiliationFilter,
      practitionerId: filtersToLoad?.practitionerId,
      clinicBrandId: filtersToLoad?.clinicBrandId,
    }
  }

  function syncReportDataFromCache(filtersToLoad) {
    const primaryCache = primaryTabCacheRef.current[getPrimaryCacheKey(filtersToLoad)] || {}
    const summaryCache = summaryTabCacheRef.current[getSummaryCacheKey(filtersToLoad)] || {}

    setReportData({
      overall: primaryCache.overall || {},
      practitioners: primaryCache.practitioners || {},
      clinicBrands: primaryCache.clinicBrands || {},
      quarterlySummary: summaryCache.quarterly?.payload || {},
      monthlySummary: summaryCache.monthly?.payload || {},
    })
    setPeriodPaging({
      quarterly: summaryCache.quarterly
        ? { nextPage: summaryCache.quarterly.nextPage, hasMore: summaryCache.quarterly.hasMore }
        : { nextPage: 0, hasMore: false },
      monthly: summaryCache.monthly
        ? { nextPage: summaryCache.monthly.nextPage, hasMore: summaryCache.monthly.hasMore }
        : { nextPage: 0, hasMore: false },
    })
  }

  async function loadPrimaryTab(tab, filtersToLoad, force = false) {
    const config = {
      overall: {
        getter: getManagementBookingReportingOverall,
        payloadKey: 'overall',
        errorMessage: 'Unable to load overall booking reporting',
      },
      practitioners: {
        getter: getManagementBookingReportingPractitioners,
        payloadKey: 'practitioners',
        errorMessage: 'Unable to load practitioner booking reporting',
      },
      clinicBrands: {
        getter: getManagementBookingReportingClinicBrands,
        payloadKey: 'clinicBrands',
        errorMessage: 'Unable to load clinic booking reporting',
      },
    }[tab]

    if (!config) {
      return
    }

    const cacheKey = getPrimaryCacheKey(filtersToLoad)
    const cachedPayload = primaryTabCacheRef.current?.[cacheKey]?.[config.payloadKey]
    if (!force && cachedPayload) {
      setReportData((current) => ({
        ...current,
        [config.payloadKey]: cachedPayload,
      }))
      return cachedPayload
    }

    setTabLoading((current) => ({ ...current, [tab]: true }))
    setError('')

    try {
      const response = await config.getter({
        from: filtersToLoad.fromDate,
        to: filtersToLoad.toDate,
        ...buildSharedParams(filtersToLoad),
      })

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(config.errorMessage)
      }

      const payload = extractPayload(response, config.payloadKey)
      primaryTabCacheRef.current[cacheKey] = {
        ...(primaryTabCacheRef.current[cacheKey] || {}),
        [config.payloadKey]: payload,
      }
      setReportData((current) => ({
        ...current,
        [config.payloadKey]: payload,
      }))
      return payload
    } catch (loadError) {
      console.error(loadError)
      setError(loadError?.message || config.errorMessage)
      return null
    } finally {
      setTabLoading((current) => ({ ...current, [tab]: false }))
    }
  }

  async function loadPeriodSummary(kind, filtersToLoad, page = 0, append = false, force = false) {
    const getter =
      kind === 'quarterly'
        ? getManagementBookingReportingQuarterlySummary
        : getManagementBookingReportingMonthlySummary
    const payloadKey = kind === 'quarterly' ? 'quarterlySummary' : 'monthlySummary'
    const summaryKey = getSummaryCacheKey(filtersToLoad)
    const cachedEntry = summaryTabCacheRef.current?.[summaryKey]?.[kind]

    if (!force && !append && page === 0 && cachedEntry?.payload) {
      setReportData((current) => ({
        ...current,
        [payloadKey]: cachedEntry.payload,
      }))
      setPeriodPaging((current) => ({
        ...current,
        [kind]: {
          nextPage: cachedEntry.nextPage,
          hasMore: cachedEntry.hasMore,
        },
      }))
      return cachedEntry.payload
    }

    setTabLoading((current) => ({ ...current, [kind]: true }))
    setError('')

    try {
      const response = await getter({
        ...buildSharedParams(filtersToLoad),
        referenceDate: summaryReferenceDate,
        page,
        size: SUMMARY_PAGE_SIZES[kind],
      })

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(
          kind === 'quarterly'
            ? 'Unable to load quarter-over-quarter history'
            : 'Unable to load month-over-month history',
        )
      }

      const payload = extractPayload(response, payloadKey)
      const incomingPeriods = Array.isArray(payload?.periods) ? payload.periods : []
      const existingPeriods = append ? cachedEntry?.payload?.periods || [] : []
      const mergedPeriods = append
        ? [
            ...existingPeriods,
            ...incomingPeriods.filter(
              (incoming) =>
                !existingPeriods.some(
                  (existing) =>
                    existing?.label === incoming?.label &&
                    existing?.fromDate === incoming?.fromDate &&
                    existing?.toDate === incoming?.toDate,
                ),
            ),
          ]
        : incomingPeriods

      const nextEntry = {
        payload: {
          ...payload,
          periods: mergedPeriods,
        },
        nextPage: page + 1,
        hasMore: incomingPeriods.length >= SUMMARY_PAGE_SIZES[kind],
      }
      summaryTabCacheRef.current[summaryKey] = {
        ...(summaryTabCacheRef.current[summaryKey] || {}),
        [kind]: nextEntry,
      }
      setReportData((current) => ({
        ...current,
        [payloadKey]: nextEntry.payload,
      }))
      setPeriodPaging((current) => ({
        ...current,
        [kind]: {
          nextPage: nextEntry.nextPage,
          hasMore: nextEntry.hasMore,
        },
      }))
      return nextEntry.payload
    } catch (loadError) {
      console.error(loadError)
      setError(
        loadError?.message ||
          (kind === 'quarterly'
            ? 'Unable to load quarter-over-quarter history'
            : 'Unable to load month-over-month history'),
      )
      return null
    } finally {
      setTabLoading((current) => ({ ...current, [kind]: false }))
    }
  }

  async function ensureSelectedTabLoaded(filtersToLoad, tab = selectedTab, force = false) {
    if (!filtersToLoad?.eventTypeFilter) {
      return
    }
    if (tab === 'quarterly' || tab === 'monthly') {
      await loadPeriodSummary(tab, filtersToLoad, 0, false, force)
      return
    }
    await loadPrimaryTab(tab, filtersToLoad, force)
  }

  async function loadFilterOptions(filtersToLoad) {
    if (
      !filtersToLoad?.eventTypeFilter ||
      !filtersToLoad?.fromDate ||
      !filtersToLoad?.toDate ||
      filtersToLoad.fromDate > filtersToLoad.toDate
    ) {
      setFilterOptions({ practitionerOptions: [], clinicBrandOptions: [] })
      return
    }

    const cacheKey = getFilterOptionsCacheKey(filtersToLoad)
    const cachedOptions = filterOptionsCacheRef.current[cacheKey]
    if (cachedOptions) {
      setFilterOptions(cachedOptions)
      return
    }

    const requestId = filterOptionsRequestRef.current + 1
    filterOptionsRequestRef.current = requestId
    setOptionsLoading(true)

    try {
      const response = await getManagementBookingReportingFilterOptions({
        from: filtersToLoad.fromDate,
        to: filtersToLoad.toDate,
        ...buildSharedParams(filtersToLoad),
      })

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error('Unable to load practitioner and clinic filters')
      }

      const payload = extractPayload(response, 'filterOptions')
      const nextOptions = {
        practitionerOptions: payload?.practitionerOptions || [],
        clinicBrandOptions: payload?.clinicBrandOptions || [],
      }
      filterOptionsCacheRef.current[cacheKey] = nextOptions
      if (filterOptionsRequestRef.current !== requestId) {
        return
      }
      setFilterOptions(nextOptions)
      setDraftFilters((current) => {
        const nextDraftFilters = { ...current }
        if (
          current.practitionerId &&
          !nextOptions.practitionerOptions.some(
            (option) => option?.practitionerId === current.practitionerId,
          )
        ) {
          nextDraftFilters.practitionerId = ''
        }
        if (
          current.clinicBrandId &&
          !nextOptions.clinicBrandOptions.some(
            (option) => option?.clinicBrandId === current.clinicBrandId,
          )
        ) {
          nextDraftFilters.clinicBrandId = ''
        }
        return nextDraftFilters
      })
    } catch (optionsError) {
      console.error(optionsError)
      if (filterOptionsRequestRef.current === requestId) {
        setFilterOptions({ practitionerOptions: [], clinicBrandOptions: [] })
      }
    } finally {
      if (filterOptionsRequestRef.current === requestId) {
        setOptionsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!draftFilters?.eventTypeFilter) {
      setFilterOptions({ practitionerOptions: [], clinicBrandOptions: [] })
      return
    }
    loadFilterOptions(draftFilters)
  }, [
    draftFilters.eventTypeFilter,
    draftFilters.dateDimension,
    draftFilters.affiliationFilter,
    draftFilters.fromDate,
    draftFilters.toDate,
    draftFilters.practitionerId,
    draftFilters.clinicBrandId,
  ])

  useEffect(() => {
    if (!appliedFilters?.eventTypeFilter) {
      return
    }
    syncReportDataFromCache(appliedFilters)
    ensureSelectedTabLoaded(appliedFilters, selectedTab)
  }, [appliedFilters, selectedTab])

  function handleApply() {
    const nextFilters = { ...draftFilters }
    setSaveMessage('')
    setError('')
    handleCloseRevenueDrawer()
    syncReportDataFromCache(nextFilters)
    setAppliedFilters(nextFilters)
  }

  async function handleLoadMorePeriods(kind) {
    if (!hasAppliedFilters || summaryLoading?.[kind] || !periodPaging?.[kind]?.hasMore) {
      return
    }
    await loadPeriodSummary(kind, appliedFilters, periodPaging[kind].nextPage, true, false)
  }

  async function handleSaveCurrentSnapshot() {
    if (!hasAppliedFilters) {
      return
    }
    setSaving(true)
    setSaveMessage('')
    setError('')

    try {
      const response = await saveManagementBookingReportingSnapshot({
        from: appliedFilters.fromDate,
        to: appliedFilters.toDate,
        ...buildSharedParams(appliedFilters),
      })

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error('Unable to save the computed report')
      }

      const savedSnapshot = extractPayload(response, 'savedSnapshot')
      setSaveMessage(
        `Saved computed report for ${savedSnapshot?.fromDate || appliedFilters.fromDate} to ${
          savedSnapshot?.toDate || appliedFilters.toDate
        } at ${savedSnapshot?.savedAt || 'now'}.`,
      )
    } catch (saveError) {
      console.error(saveError)
      setError(saveError?.message || 'Unable to save the computed report')
    } finally {
      setSaving(false)
    }
  }

  async function handleRefreshCompletedSnapshots() {
    setRefreshingSnapshots(true)
    setSaveMessage('')
    setError('')

    try {
      const response = await refreshManagementBookingReportingSnapshots()

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error('Unable to refresh monthly and quarterly snapshots')
      }

      summaryTabCacheRef.current = {}
      if (hasAppliedFilters) {
        syncReportDataFromCache(appliedFilters)
        await Promise.all([
          loadPeriodSummary('quarterly', appliedFilters, 0, false, true),
          loadPeriodSummary('monthly', appliedFilters, 0, false, true),
        ])
      }
      const refreshStatus = extractPayload(response, 'refreshStatus')
      setSaveMessage(
        `Refreshed QoQ and MoM snapshots at ${refreshStatus?.refreshedAt || 'now'}.`,
      )
    } catch (refreshError) {
      console.error(refreshError)
      setError(refreshError?.message || 'Unable to refresh monthly and quarterly snapshots')
    } finally {
      setRefreshingSnapshots(false)
    }
  }

  function handleCloseRevenueDrawer() {
    setRevenueDrawer({
      open: false,
      loading: false,
      error: '',
      payload: null,
    })
  }

  async function handleOpenRevenueDrawer(row) {
    if (!row?.practitionerId || !hasAppliedFilters) {
      return
    }

    setRevenueDrawer({
      open: true,
      loading: true,
      error: '',
      payload: {
        practitionerId: row.practitionerId,
        practitionerName: row.practitionerName,
        practitionerRoles: row.practitionerRoles,
        summary: {
          revenueCollected: row.revenueCollected,
          currencyCode: row.currencyCode || 'INR',
        },
        events: [],
      },
    })

    try {
      const response = await getManagementBookingReportingPractitionerRevenueDetails({
        from: appliedFilters.fromDate,
        to: appliedFilters.toDate,
        ...buildSharedParams(appliedFilters),
        practitionerId: row.practitionerId,
      })

      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error('Unable to load practitioner revenue details')
      }

      setRevenueDrawer({
        open: true,
        loading: false,
        error: '',
        payload: extractPayload(response, 'practitionerRevenueDetails'),
      })
    } catch (drawerError) {
      console.error(drawerError)
      setRevenueDrawer((current) => ({
        ...current,
        loading: false,
        error: drawerError?.message || 'Unable to load practitioner revenue details',
      }))
    }
  }

  function formatSessionSequence(row) {
    if (!row?.patientMeetingSeqNumber) {
      return row?.sessionCohortLabel || '-'
    }
    return `Session ${formatNumber(row.patientMeetingSeqNumber)} (${row?.sessionCohortLabel || 'Unknown cohort'})`
  }

  function buildRevenueDetailExportRows(events = []) {
    return events.map((row) => ({
      event: row?.eventName || row?.bookedEventId || '-',
      patientAndSession: `${row?.patientName || '-'} | ${formatSessionSequence(row)}`,
      clinic: [row?.clinicBrandName, row?.clinicLocationName].filter(Boolean).join(' / ') || '-',
      scheduleAndStatus: `${formatDateTime(row?.scheduledAt)} | ${
        row?.started ? 'Started' : 'Not started'
      } / ${row?.completed ? 'Completed' : 'Incomplete'}`,
      revenueCollected: formatCurrency(row?.revenueCollected || 0, row?.currencyCode || 'INR'),
    }))
  }

  async function handleExportRevenueDetails(exportType) {
    if (!revenueDrawer?.payload || !revenueDrawer?.payload?.events?.length) {
      return
    }

    const exportTitle = sanitizeFileTitle(
      `management-reporting-bookings-${
        revenueDrawer.payload.practitionerName || 'practitioner'
      }-revenue-details-${dayjs().format('YYYYMMDD-HHmm')}`,
    )

    try {
      if (exportType === 'excel') {
        await downloadExcelSheetFromRows({
          rows: buildRevenueDetailExportRows(revenueDrawer.payload.events),
          titleOfSheet: exportTitle,
          workSheetTitle: 'Revenue details',
        })
        return
      }

      if (exportType === 'pdf') {
        await exportDomAsPdf(revenueExportRef.current, exportTitle)
      }
    } catch (exportError) {
      console.error(exportError)
      setError(exportError?.message || 'Unable to export revenue details')
    }
  }

  const practitionerColumns = [
    { key: 'practitionerName', label: 'Practitioner', type: 'text' },
    { key: 'practitionerRoles', label: 'Roles', type: 'text' },
    { key: 'totalBookings', label: 'Bookings', type: 'number' },
    {
      key: 'startedCompleted',
      label: 'Started / Completed',
      metricKey: 'startedCompleted',
      render: (row) => renderStartedCompletedCell(row),
      exportValue: (row) => formatStartedCompletedExport(row),
    },
    { key: 'neverStartedSessions', label: 'Never started', type: 'number' },
    { key: 'onePartyJoinedRate', label: 'One-party joined', type: 'rate' },
    {
      key: 'joinDelays',
      label: 'Join delays',
      metricKey: 'joinDelays',
      render: (row) => renderJoinDelayCell(row),
      exportValue: (row) =>
        `Patient ${formatValue(row?.avgPatientJoinDelayMinutes, 'number')} / Practitioner ${formatValue(
          row?.avgPractitionerJoinDelayMinutes,
          'number',
        )}`,
    },
    {
      key: 'revenueCollected',
      label: 'Revenue collected',
      metricKey: 'revenueCollectedDetails',
      render: (row) => {
        const amount = Number(row?.revenueCollected || 0)
        const currencyCode = row?.currencyCode || 'INR'
        if (!row?.practitionerId) {
          return formatCurrency(amount, currencyCode)
        }
        return (
          <Button
            variant="text"
            size="small"
            onClick={() => handleOpenRevenueDrawer(row)}
            sx={{
              p: 0,
              minWidth: 0,
              justifyContent: 'flex-start',
              textTransform: 'none',
              textAlign: 'left',
              alignItems: 'flex-start',
            }}
          >
            <Stack spacing={0} alignItems="flex-start">
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'underline' }}
              >
                {formatCurrency(amount, currencyCode)}
              </Typography>
              <Typography variant="caption" component="span" sx={{ color: 'primary.main' }}>
                View booked events
              </Typography>
            </Stack>
          </Button>
        )
      },
      exportValue: (row) => formatCurrency(row?.revenueCollected || 0, row?.currencyCode || 'INR'),
    },
    { key: 'revenueToAumhum', label: 'Revenue to AUMHUM', type: 'currency' },
    { key: 'payoutToPractitioner', label: 'Payout to practitioner', type: 'currency' },
    { key: 'distinctPatients', label: 'Patients', type: 'number' },
    {
      key: 'oneSessionOnlyPatients',
      label: '1 session only',
      render: (row) => renderCohortThresholdCell(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare'),
    },
    {
      key: 'repeatPatients',
      label: '2+ sessions (repeat)',
      render: (row) => renderCohortThresholdCell(row, 'repeatPatients', 'repeatPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'repeatPatients', 'repeatPatientShare'),
    },
    {
      key: 'fourPlusSessionPatients',
      label: '4+ sessions',
      render: (row) => renderCohortThresholdCell(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare'),
    },
    {
      key: 'longTermCarePatients',
      label: '10+ sessions',
      render: (row) => renderCohortThresholdCell(row, 'longTermCarePatients', 'longTermCarePatientShare'),
      exportValue: (row) => formatCohortExport(row, 'longTermCarePatients', 'longTermCarePatientShare'),
    },
    { key: 'avgSessionsPerPatient', label: 'Avg sessions / patient', type: 'number' },
  ]

  const clinicBrandColumns = [
    { key: 'clinicBrandName', label: 'Clinic brand', type: 'text' },
    {
      key: 'clinicLocationName',
      label: 'Scope',
      metricKey: 'scope',
      render: renderScopeCell,
      exportValue: (row) => (row?.rowType === 'brandTotal' ? 'Brand total' : row?.clinicLocationName || '-'),
    },
    { key: 'totalBookings', label: 'Bookings', type: 'number' },
    {
      key: 'startedCompleted',
      label: 'Started / Completed',
      metricKey: 'startedCompleted',
      render: (row) => renderStartedCompletedCell(row),
      exportValue: (row) => formatStartedCompletedExport(row),
    },
    { key: 'neverStartedSessions', label: 'Never started', type: 'number' },
    { key: 'onePartyJoinedRate', label: 'One-party joined', type: 'rate' },
    { key: 'revenueCollected', label: 'Revenue collected', type: 'currency' },
    { key: 'revenueToAumhum', label: 'Revenue to AUMHUM', type: 'currency' },
    { key: 'payoutToPractitioner', label: 'Payout to practitioner', type: 'currency' },
    { key: 'distinctPatients', label: 'Patients', type: 'number' },
    {
      key: 'oneSessionOnlyPatients',
      label: '1 session only',
      render: (row) => renderCohortThresholdCell(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare'),
    },
    {
      key: 'repeatPatients',
      label: '2+ sessions (repeat)',
      render: (row) => renderCohortThresholdCell(row, 'repeatPatients', 'repeatPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'repeatPatients', 'repeatPatientShare'),
    },
    {
      key: 'fourPlusSessionPatients',
      label: '4+ sessions',
      render: (row) => renderCohortThresholdCell(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare'),
      exportValue: (row) => formatCohortExport(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare'),
    },
    {
      key: 'longTermCarePatients',
      label: '10+ sessions',
      render: (row) => renderCohortThresholdCell(row, 'longTermCarePatients', 'longTermCarePatientShare'),
      exportValue: (row) => formatCohortExport(row, 'longTermCarePatients', 'longTermCarePatientShare'),
    },
    { key: 'avgSessionsPerPatient', label: 'Avg sessions / patient', type: 'number' },
  ]

  const periodColumns = [
    { key: 'label', label: 'Period', type: 'text', metricKey: 'periodLabel' },
    {
      key: 'window',
      label: 'Window',
      metricKey: 'periodWindow',
      render: (row) => `${row?.fromDate || '-'} to ${row?.toDate || '-'}`,
      exportValue: (row) => `${row?.fromDate || '-'} to ${row?.toDate || '-'}`,
    },
    { key: 'totalBookings', label: 'Bookings', type: 'number', sourceKey: 'summary' },
    {
      key: 'startedCompleted',
      label: 'Started / Completed',
      metricKey: 'startedCompleted',
      sourceKey: 'summary',
      render: (row) => renderStartedCompletedCell(row, 'summary'),
      exportValue: (row) => formatStartedCompletedExport(row, 'summary'),
    },
    { key: 'neverStartedSessions', label: 'Never started', type: 'number', sourceKey: 'summary' },
    { key: 'onePartyJoinedRate', label: 'One-party joined', type: 'rate', sourceKey: 'summary' },
    { key: 'revenueCollected', label: 'Revenue collected', type: 'currency', sourceKey: 'summary' },
    { key: 'revenueToAumhum', label: 'Revenue to AUMHUM', type: 'currency', sourceKey: 'summary' },
    { key: 'payoutToPractitioner', label: 'Payout to practitioner', type: 'currency', sourceKey: 'summary' },
    { key: 'distinctPatients', label: 'Patients', type: 'number', sourceKey: 'summary' },
    {
      key: 'oneSessionOnlyPatients',
      label: '1 session only',
      sourceKey: 'summary',
      render: (row) =>
        renderCohortThresholdCell(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare', 'summary'),
      exportValue: (row) =>
        formatCohortExport(row, 'oneSessionOnlyPatients', 'oneSessionOnlyPatientShare', 'summary'),
    },
    {
      key: 'repeatPatients',
      label: '2+ sessions (repeat)',
      sourceKey: 'summary',
      render: (row) => renderCohortThresholdCell(row, 'repeatPatients', 'repeatPatientShare', 'summary'),
      exportValue: (row) => formatCohortExport(row, 'repeatPatients', 'repeatPatientShare', 'summary'),
    },
    {
      key: 'fourPlusSessionPatients',
      label: '4+ sessions',
      sourceKey: 'summary',
      render: (row) =>
        renderCohortThresholdCell(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare', 'summary'),
      exportValue: (row) =>
        formatCohortExport(row, 'fourPlusSessionPatients', 'fourPlusSessionPatientShare', 'summary'),
    },
    {
      key: 'longTermCarePatients',
      label: '10+ sessions',
      sourceKey: 'summary',
      render: (row) => renderCohortThresholdCell(row, 'longTermCarePatients', 'longTermCarePatientShare', 'summary'),
      exportValue: (row) =>
        formatCohortExport(row, 'longTermCarePatients', 'longTermCarePatientShare', 'summary'),
    },
    {
      key: 'servedFromCache',
      label: 'Source',
      metricKey: 'servedFromCache',
      render: (row) =>
        row?.servedFromCache ? (
          <Chip size="small" label="Cached" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Live" color="warning" variant="outlined" />
        ),
      exportValue: (row) => (row?.servedFromCache ? 'Cached' : 'Live'),
    },
  ]

  async function handleExportCurrentTab(exportType) {
    if (!hasAppliedFilters) {
      return
    }

    const currentTabRef = {
      overall: overallExportRef,
      practitioners: practitionersExportRef,
      clinicBrands: clinicsExportRef,
      quarterly: quarterlyExportRef,
      monthly: monthlyExportRef,
    }[selectedTab]

    const currentTabRows = {
      overall: buildOverallExportRows(overallSummary),
      practitioners: practitionerRows.map((row) =>
        Object.fromEntries(practitionerColumns.map((column) => [column.label, resolveColumnExportValue(row, column)])),
      ),
      clinicBrands: clinicBrandRows.map((row) =>
        Object.fromEntries(clinicBrandColumns.map((column) => [column.label, resolveColumnExportValue(row, column)])),
      ),
      quarterly: buildSummaryExportRows(quarterlyRows),
      monthly: buildSummaryExportRows(monthlyRows),
    }[selectedTab]

    const appliedEventTypeLabel = sanitizeFileTitle(getEventFilterLabel(appliedFilters?.eventTypeFilter))
    const exportTitle = sanitizeFileTitle(
      `management-reporting-bookings-${selectedTab}-${appliedEventTypeLabel}-${dayjs().format('YYYYMMDD-HHmm')}`,
    )

    try {
      if (exportType === 'excel') {
        await downloadExcelSheetFromRows({
          rows: currentTabRows || [],
          titleOfSheet: exportTitle,
          workSheetTitle: TAB_OPTIONS.find((tab) => tab.value === selectedTab)?.label || 'Bookings',
        })
        return
      }

      if (exportType === 'pdf') {
        await exportDomAsPdf(currentTabRef?.current, exportTitle)
      }
    } catch (exportError) {
      console.error(exportError)
      setError(exportError?.message || 'Unable to export the current tab')
    }
  }

  const currentTabHasData =
    selectedTab === 'overall'
      ? Object.keys(overallSummary || {}).length > 0
      : selectedTab === 'practitioners'
        ? practitionerRows.length > 0
        : selectedTab === 'clinicBrands'
          ? clinicBrandRows.length > 0
          : selectedTab === 'quarterly'
            ? quarterlyRows.length > 0
            : monthlyRows.length > 0

  const quarterlyFooter = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`Loaded quarters: ${formatNumber(quarterlyRows.length)}`} variant="outlined" />
        <Chip
          label={
            summaryLoading.quarterly
              ? 'Loading more completed-quarter history...'
              : 'Completed-quarter history only. From / To do not affect this tab.'
          }
          variant="outlined"
        />
      </Stack>
      <Button
        variant="outlined"
        disabled={!hasAppliedFilters || summaryLoading.quarterly || !periodPaging.quarterly.hasMore}
        onClick={() => handleLoadMorePeriods('quarterly')}
      >
        {summaryLoading.quarterly ? 'Loading...' : periodPaging.quarterly.hasMore ? 'Load older quarters' : 'No older quarters'}
      </Button>
    </Stack>
  )

  const monthlyFooter = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`Loaded months: ${formatNumber(monthlyRows.length)}`} variant="outlined" />
        <Chip
          label={
            summaryLoading.monthly
              ? 'Loading more completed-month history...'
              : 'Completed-month history only. From / To do not affect this tab.'
          }
          variant="outlined"
        />
      </Stack>
      <Button
        variant="outlined"
        disabled={!hasAppliedFilters || summaryLoading.monthly || !periodPaging.monthly.hasMore}
        onClick={() => handleLoadMorePeriods('monthly')}
      >
        {summaryLoading.monthly ? 'Loading...' : periodPaging.monthly.hasMore ? 'Load older months' : 'No older months'}
      </Button>
    </Stack>
  )

  const currentTabLoading =
    selectedTab === 'overall'
      ? tabLoading.overall && !reportData?.overall?.summary
      : selectedTab === 'practitioners'
        ? tabLoading.practitioners && practitionerRows.length === 0
        : selectedTab === 'clinicBrands'
          ? tabLoading.clinicBrands && clinicBrandRows.length === 0
          : selectedTab === 'quarterly'
            ? tabLoading.quarterly && quarterlyRows.length === 0
            : tabLoading.monthly && monthlyRows.length === 0

  return (
    <>
      <SupportHeader heading="Management Reporting" subHeading="Bookings" />
      <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(14, 116, 144, 0.08) 100%)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Booking reporting filters
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Select an event type to start. From and To affect Overall, Practitioner, and Clinic Brand. QoQ and MoM are independent completed-period histories for the selected event type and ignore the date range.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl fullWidth>
                <InputLabel id="management-reporting-event-type-label">Event type</InputLabel>
                <Select
                  labelId="management-reporting-event-type-label"
                  label="Event type"
                  value={draftFilters.eventTypeFilter}
                  onChange={(event) => updateDraftFilter('eventTypeFilter', event.target.value)}
                >
                  {EVENT_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'select-event-type'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="From"
                type="date"
                value={draftFilters.fromDate}
                onChange={(event) => updateDraftFilter('fromDate', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="To"
                type="date"
                value={draftFilters.toDate}
                onChange={(event) => updateDraftFilter('toDate', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="management-reporting-date-dimension-label">Date basis</InputLabel>
                <Select
                  labelId="management-reporting-date-dimension-label"
                  label="Date basis"
                  value={draftFilters.dateDimension}
                  onChange={(event) => updateDraftFilter('dateDimension', event.target.value)}
                >
                  {DATE_DIMENSION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="management-reporting-affiliation-label">Affiliation</InputLabel>
                <Select
                  labelId="management-reporting-affiliation-label"
                  label="Affiliation"
                  value={draftFilters.affiliationFilter}
                  onChange={(event) => updateDraftFilter('affiliationFilter', event.target.value)}
                >
                  {AFFILIATION_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Autocomplete
                fullWidth
                options={filterOptions.practitionerOptions}
                loading={optionsLoading}
                value={
                  filterOptions.practitionerOptions.find(
                    (option) => option?.practitionerId === draftFilters.practitionerId,
                  ) || null
                }
                onChange={(_, value) => updateDraftFilter('practitionerId', value?.practitionerId || '')}
                isOptionEqualToValue={(option, value) => option?.practitionerId === value?.practitionerId}
                getOptionLabel={getPractitionerFilterOptionLabel}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Typography variant="body2">{getPractitionerFilterOptionLabel(option)}</Typography>
                      {option?.bookingCount ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {getMatchingBookingsLabel(option)}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                )}
                noOptionsText={
                  draftFilters.eventTypeFilter
                    ? 'No practitioners found for this filter set'
                    : 'Select event type to load practitioners'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Practitioner"
                    helperText={
                      draftFilters.eventTypeFilter
                        ? 'Linked to event type and clinic selection'
                        : 'Select event type to start'
                    }
                  />
                )}
                disabled={!draftFilters.eventTypeFilter}
              />
              <Autocomplete
                fullWidth
                options={filterOptions.clinicBrandOptions}
                loading={optionsLoading}
                value={
                  filterOptions.clinicBrandOptions.find(
                    (option) => option?.clinicBrandId === draftFilters.clinicBrandId,
                  ) || null
                }
                onChange={(_, value) => updateDraftFilter('clinicBrandId', value?.clinicBrandId || '')}
                isOptionEqualToValue={(option, value) => option?.clinicBrandId === value?.clinicBrandId}
                getOptionLabel={getClinicBrandFilterOptionLabel}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Typography variant="body2">{getClinicBrandFilterOptionLabel(option)}</Typography>
                      {option?.bookingCount ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {getMatchingBookingsLabel(option)}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                )}
                noOptionsText={
                  draftFilters.eventTypeFilter
                    ? 'No clinic brands found for this filter set'
                    : 'Select event type to load clinics'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clinic brand"
                    helperText={
                      draftFilters.eventTypeFilter
                        ? 'Linked to event type and practitioner selection'
                        : 'Select event type to start'
                    }
                  />
                )}
                disabled={!draftFilters.eventTypeFilter}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>

              <Button
                variant="contained"
                onClick={handleApply}
                disabled={applyDisabled}
                sx={{
                  minWidth: { md: 170 },
                  height: 56,
                  borderRadius: 2.5,
                  ...(filtersDirty && !applyDisabled
                    ? {
                        animation: 'bookingReportingPulse 1.1s ease-in-out infinite',
                        boxShadow: '0 0 0 0 rgba(14, 116, 144, 0.45)',
                        '@keyframes bookingReportingPulse': {
                          '0%': {
                            transform: 'scale(1)',
                            boxShadow: '0 0 0 0 rgba(14, 116, 144, 0.45)',
                          },
                          '70%': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 0 0 12px rgba(14, 116, 144, 0)',
                          },
                          '100%': {
                            transform: 'scale(1)',
                            boxShadow: '0 0 0 0 rgba(14, 116, 144, 0)',
                          },
                        },
                      }
                    : {}),
                }}
              >
                {loading ? 'Loading...' : filtersDirty ? 'Apply filters' : hasAppliedFilters ? 'Applied' : 'Apply filters'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefreshCompletedSnapshots}
                disabled={refreshingSnapshots || saving}
                sx={{ minWidth: { md: 250 }, height: 56, borderRadius: 2.5 }}
              >
                {refreshingSnapshots ? 'Refreshing snapshots...' : 'Refresh QoQ/MoM snapshots'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveCurrentSnapshot}
                disabled={saving || loading || !hasAppliedFilters || filtersDirty}
                sx={{ minWidth: { md: 190 }, height: 56, borderRadius: 2.5 }}
              >
                {saving ? 'Saving...' : 'Save computed report'}
              </Button>
              <ExportTypeSelector
                hideOptionsId={['email_pdf']}
                handleSelectExportType={handleExportCurrentTab}
                mainButton={({ handleClick, isOpen }) => (
                  <Button
                    id="management-reporting-export-button"
                    aria-controls={isOpen ? 'export-type-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={isOpen ? 'true' : undefined}
                    variant="outlined"
                    onClick={handleClick}
                    disabled={!hasAppliedFilters || currentTabLoading || !currentTabHasData}
                    sx={{ minWidth: { md: 170 }, height: 56, borderRadius: 2.5 }}
                  >
                    Download
                  </Button>
                )}
              />
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={
                  hasAppliedFilters
                    ? `Event type: ${getEventFilterLabel(appliedFilters.eventTypeFilter)}`
                    : 'Event type: Select event type to start'
                }
              />
              <Chip label={`Default range: ${latestCompletedMonthWindow.fromDate} to ${latestCompletedMonthWindow.toDate}`} />
              <Chip
                label={`Date basis: ${
                  hasAppliedFilters
                    ? getDateDimensionLabel(appliedFilters.dateDimension)
                    : getDateDimensionLabel(draftFilters.dateDimension)
                }`}
              />
              <Chip
                label={`Affiliation: ${
                  hasAppliedFilters
                    ? getAffiliationFilterLabel(appliedFilters.affiliationFilter)
                    : getAffiliationFilterLabel(draftFilters.affiliationFilter)
                }`}
              />
              {hasAppliedFilters && appliedFilters?.practitionerId ? (
                <Chip
                  label={`Practitioner: ${
                    practitionerRows.find((row) => row?.practitionerId === appliedFilters.practitionerId)?.practitionerName ||
                    filterOptions.practitionerOptions.find(
                      (option) => option?.practitionerId === appliedFilters.practitionerId,
                    )?.practitionerName ||
                    appliedFilters.practitionerId
                  }`}
                />
              ) : null}
              {hasAppliedFilters && appliedFilters?.clinicBrandId ? (
                <Chip
                  label={`Clinic brand: ${
                    clinicBrandRows.find((row) => row?.clinicBrandId === appliedFilters.clinicBrandId)?.clinicBrandName ||
                    filterOptions.clinicBrandOptions.find(
                      (option) => option?.clinicBrandId === appliedFilters.clinicBrandId,
                    )?.clinicBrandName ||
                    appliedFilters.clinicBrandId
                  }`}
                />
              ) : null}
              <Chip label="QoQ and MoM ignore From / To" variant="outlined" />
              {reportData?.quarterlySummary?.cachedPeriods ? (
                <Chip
                  label={`QoQ cached periods: ${reportData.quarterlySummary.cachedPeriods}`}
                  color="success"
                  variant="outlined"
                />
              ) : null}
              {reportData?.monthlySummary?.cachedPeriods ? (
                <Chip
                  label={`MoM cached periods: ${reportData.monthlySummary.cachedPeriods}`}
                  color="success"
                  variant="outlined"
                />
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        {filtersDirty ? (
          <Alert severity="warning">
            Filter values changed. Apply is highlighted so you can refresh the report with the new selection.
          </Alert>
        ) : null}
        {!hasAppliedFilters ? (
          <Alert severity="info">
            Select an event type and apply filters to start. No report is triggered automatically before event-type selection.
          </Alert>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {hasAppliedFilters && reportData?.overall?.joinMetricsNote ? (
          <Alert severity="info">{reportData.overall.joinMetricsNote}</Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)} variant="scrollable" scrollButtons="auto">
            {TAB_OPTIONS.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Paper>

        {!hasAppliedFilters ? null : currentTabLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {hasAppliedFilters && !currentTabLoading && selectedTab === 'overall' ? (
          <Box ref={overallExportRef} sx={{ display: 'grid', gap: 3 }}>
            <MetricCards title="Volume" fields={SUMMARY_CARD_FIELDS} summary={overallSummary} />
            <MetricCards
              title="Conversion & reliability"
              subtitle="This keeps the funnel readable while still showing operational quality markers that explain completion."
              fields={RATE_CARD_FIELDS}
              summary={overallSummary}
            />
            <MetricCards
              title="Join behavior"
              subtitle="Join metrics come from BookedEventLog and help separate attendance friction from downstream completion issues."
              fields={JOIN_CARD_FIELDS}
              summary={overallSummary}
            />
            <MetricCards title="Revenue" fields={REVENUE_CARD_FIELDS} summary={overallSummary} />
            <MetricCards
              title="Patient continuity"
              subtitle="These use booked-status cohort logic so session depth stays meaningful even when cancellations exist in the same slice."
              fields={PATIENT_CONTINUITY_FIELDS}
              summary={overallSummary}
            />
            <PatientDistributionPanel summary={overallSummary} />
          </Box>
        ) : null}

        {hasAppliedFilters && !currentTabLoading && selectedTab === 'practitioners' ? (
          <DataTable
            surfaceRef={practitionersExportRef}
            title="Practitioner-wise breakdown"
            subtitle="Only approved doctors, therapists, and screeners relevant to the selected event filter are included here."
            rows={practitionerRows}
            columns={practitionerColumns}
            getRowKey={(row, index) => `${row?.practitionerId || 'practitioner'}-${index}`}
          />
        ) : null}

        {hasAppliedFilters && !currentTabLoading && selectedTab === 'clinicBrands' ? (
          <DataTable
            surfaceRef={clinicsExportRef}
            title="Clinic-brand and clinic-location breakdown"
            subtitle="Each clinic brand shows a total row first, followed by its clinic-location rows for the same reporting window."
            rows={clinicBrandRows}
            columns={clinicBrandColumns}
            getRowKey={(row, index) =>
              `${row?.clinicBrandId || 'brand'}-${row?.clinicLocationId || row?.rowType || 'row'}-${index}`
            }
            getRowSx={(row) =>
              row?.rowType === 'brandTotal'
                ? {
                    backgroundColor: 'rgba(14, 116, 144, 0.05)',
                    '& td': { fontWeight: 700 },
                  }
                : undefined
            }
          />
        ) : null}

        {hasAppliedFilters && !currentTabLoading && selectedTab === 'quarterly' ? (
          <DataTable
            surfaceRef={quarterlyExportRef}
            title="Quarter-over-quarter summary"
            subtitle={`Completed quarter history only for ${getEventFilterLabel(appliedFilters.eventTypeFilter)}. This tab is independent of From and To and starts from the latest completed quarter.`}
            rows={quarterlyRows}
            columns={periodColumns}
            getRowKey={(row, index) => `${row?.label || 'quarter'}-${index}`}
            footer={quarterlyFooter}
            emptyMessage="No completed-quarter history found for the selected event type."
          />
        ) : null}

        {hasAppliedFilters && !currentTabLoading && selectedTab === 'monthly' ? (
          <DataTable
            surfaceRef={monthlyExportRef}
            title="Month-over-month summary"
            subtitle={`Completed month history only for ${getEventFilterLabel(appliedFilters.eventTypeFilter)}. This tab is independent of From and To and starts from the latest completed month.`}
            rows={monthlyRows}
            columns={periodColumns}
            getRowKey={(row, index) => `${row?.label || 'month'}-${index}`}
            footer={monthlyFooter}
            emptyMessage="No completed-month history found for the selected event type."
          />
        ) : null}
      </Box>

      <Drawer
        anchor="right"
        open={revenueDrawer.open}
        onClose={handleCloseRevenueDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', lg: 1240 },
            p: 0,
          },
        }}
      >
        <Box sx={{ p: 3, display: 'grid', gap: 2 }} ref={revenueExportRef}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Revenue collected details
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {revenueDrawer?.payload?.practitionerName || 'Practitioner'}
                {revenueDrawer?.payload?.practitionerRoles ? ` • ${revenueDrawer.payload.practitionerRoles}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <ExportTypeSelector
                hideOptionsId={['email_pdf']}
                handleSelectExportType={handleExportRevenueDetails}
                mainButton={({ handleClick, isOpen }) => (
                  <Button
                    id="management-reporting-revenue-export-button"
                    aria-controls={isOpen ? 'export-type-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={isOpen ? 'true' : undefined}
                    variant="outlined"
                    onClick={handleClick}
                    disabled={revenueDrawer.loading || !practitionerRevenueEvents.length}
                  >
                    Download
                  </Button>
                )}
              />
              <Button variant="outlined" onClick={handleCloseRevenueDrawer}>
                Close
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Revenue collected: ${formatCurrency(
                revenueDrawer?.payload?.summary?.revenueCollected || 0,
                revenueDrawer?.payload?.summary?.currencyCode || 'INR',
              )}`}
            />
            <Chip label={`Booked events: ${formatNumber(practitionerRevenueEvents.length)}`} />
          </Stack>

          {revenueDrawer.error ? <Alert severity="error">{revenueDrawer.error}</Alert> : null}

          {revenueDrawer.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Patient & session</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Clinic</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Schedule & status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Revenue collected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {practitionerRevenueEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          No collected-revenue events found for this practitioner in the selected window.
                        </TableCell>
                      </TableRow>
                    ) : (
                      practitionerRevenueEvents.map((row, index) => (
                        <TableRow key={`${row?.bookedEventId || 'event'}-${index}`} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row?.eventName || row?.bookedEventId || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row?.eventTypeLabel || '-'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row?.patientName || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatSessionSequence(row)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row?.clinicBrandName || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row?.clinicLocationName || '-'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatDateTime(row?.scheduledAt)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row?.started ? 'Started' : 'Not started'} / {row?.completed ? 'Completed' : 'Incomplete'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            {formatCurrency(row?.revenueCollected || 0, row?.currencyCode || 'INR')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Drawer>
    </>
  )
}
